import { resolveItemImage } from "@/helpers/itemImage"
import { formatItemStatValue, statUnitFor } from "@/helpers/statFormat"
import { IStatEntry } from "@/store/apis/catalogsAPI/catalogs.types"
import {
  IActiveEffectType,
  IHeroActiveEffect
} from "@/store/apis/heroAPI/hero.types"
import {
  IActiveEffectStatView,
  IActiveEffectView
} from "@/types/activeEffects.types"

export const MAX_TIMER_DELAY_MS = 24 * 60 * 60 * 1000
export const MIN_EXPIRY_REFETCH_INTERVAL_MS = 30_000

const MS_PER_SECOND = 1000
const SECONDS_PER_MINUTE = 60
const MINUTES_PER_HOUR = 60
const HOURS_PER_DAY = 24
const SECONDS_PER_HOUR = SECONDS_PER_MINUTE * MINUTES_PER_HOUR
const SECONDS_PER_DAY = SECONDS_PER_HOUR * HOURS_PER_DAY

export interface IEffectStatView {
  effectCode: string
  effectType: IActiveEffectType
  statCode: string
  expiresAt: string
  text: string
  isPoints: boolean
  isNegative: boolean
}

export interface IRemainingParts {
  days: number | null
  clock: string
}

export interface IInvalidationMark {
  targetMs: number
  atMs: number
}

type INamedEffect = Pick<IHeroActiveEffect, "effectCode" | "effectType">

export const effectNameKey = (effect: INamedEffect): string =>
  effect.effectType === "ELIXIR"
    ? `inventory:item.${effect.effectCode}.name`
    : effect.effectCode

const effectIconUrl = (effect: INamedEffect): string | undefined =>
  effect.effectType === "ELIXIR"
    ? resolveItemImage(effect.effectCode)
    : undefined

const parseExpiresAt = (expiresAt: string): number | null => {
  const endMs = Date.parse(expiresAt)
  return Number.isNaN(endMs) ? null : endMs
}

export const remainingMs = (endMs: number, nowMs: number): number =>
  Math.max(0, endMs - nowMs)

const pad2 = (value: number): string => String(value).padStart(2, "0")

export const formatRemaining = (remaining: number): IRemainingParts => {
  const totalSeconds = Math.floor(Math.max(0, remaining) / MS_PER_SECOND)
  const days = Math.floor(totalSeconds / SECONDS_PER_DAY)
  const hours = Math.floor(totalSeconds / SECONDS_PER_HOUR) % HOURS_PER_DAY
  const minutes =
    Math.floor(totalSeconds / SECONDS_PER_MINUTE) % MINUTES_PER_HOUR
  const seconds = totalSeconds % SECONDS_PER_MINUTE
  return {
    days: days > 0 ? days : null,
    clock: `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`
  }
}

export const buildEffectStatViews = (
  rows: IHeroActiveEffect[] | undefined,
  statCatalog: IStatEntry[]
): IEffectStatView[] => {
  if (!Array.isArray(rows)) {
    return []
  }
  return rows.flatMap((row): IEffectStatView[] => {
    if (parseExpiresAt(row.expiresAt) === null) {
      return []
    }
    const formatted = formatItemStatValue(
      row.value,
      statUnitFor(statCatalog, row.statCode),
      row.operation
    )
    return [
      {
        effectCode: row.effectCode,
        effectType: row.effectType,
        statCode: row.statCode,
        expiresAt: row.expiresAt,
        text: formatted.text,
        isPoints: formatted.note === "points",
        isNegative: row.value < 0
      }
    ]
  })
}

export const projectActiveEffects = (
  statViews: IEffectStatView[],
  nowMs: number
): IActiveEffectView[] => {
  const groups = new Map<string, { view: IEffectStatView; endMs: number }[]>()
  for (const view of statViews) {
    const endMs = parseExpiresAt(view.expiresAt)
    if (endMs === null || remainingMs(endMs, nowMs) === 0) {
      continue
    }
    const group = groups.get(view.effectCode)
    if (group) {
      group.push({ view, endMs })
    } else {
      groups.set(view.effectCode, [{ view, endMs }])
    }
  }
  return [...groups.values()].map((group): IActiveEffectView => {
    const endMs = Math.max(...group.map((row) => row.endMs))
    const { days, clock } = formatRemaining(remainingMs(endMs, nowMs))
    return {
      effectCode: group[0].view.effectCode,
      iconUrl: effectIconUrl(group[0].view),
      nameKey: effectNameKey(group[0].view),
      stats: group.map(
        ({ view }): IActiveEffectStatView => ({
          statCode: view.statCode,
          text: view.text,
          isPoints: view.isPoints,
          isNegative: view.isNegative
        })
      ),
      days,
      clock
    }
  })
}

export const nextTickMs = (
  statViews: IEffectStatView[],
  nowMs: number
): number | null => {
  const hasLiveRow = statViews.some((view) => {
    const endMs = parseExpiresAt(view.expiresAt)
    return endMs !== null && remainingMs(endMs, nowMs) > 0
  })
  return hasLiveRow ? nowMs - (nowMs % MS_PER_SECOND) + MS_PER_SECOND : null
}

export const earliestExpiryMs = (
  rows: IHeroActiveEffect[] | undefined
): number | null => {
  if (!Array.isArray(rows)) {
    return null
  }
  let earliest: number | null = null
  for (const row of rows) {
    const endMs = parseExpiresAt(row.expiresAt)
    if (endMs === null) {
      continue
    }
    if (earliest === null || endMs < earliest) {
      earliest = endMs
    }
  }
  return earliest
}

export const distinctEffectCodes = (
  rows: IHeroActiveEffect[] | undefined
): string[] =>
  Array.isArray(rows) ? [...new Set(rows.map((row) => row.effectCode))] : []

export const nextTimerDelayMs = (
  targetMs: number,
  nowMs: number,
  minDelayMs: number
): number => {
  const left = targetMs - nowMs
  return Math.min(MAX_TIMER_DELAY_MS, left > 0 ? Math.max(1, left) : minDelayMs)
}

export const shouldInvalidateExpiry = (
  last: IInvalidationMark,
  targetMs: number,
  nowMs: number
): boolean =>
  nowMs >= targetMs &&
  (last.targetMs !== targetMs ||
    nowMs - last.atMs >= MIN_EXPIRY_REFETCH_INTERVAL_MS)
